import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from "../../context/DBContext";
import { useNavigate } from "react-router-dom";
import { BuildingOfficeIcon, EnvelopeIcon, LockClosedIcon, EyeIcon, EyeSlashIcon, MapPinIcon, XMarkIcon, BanknotesIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

const validatePasswordStrength = (password) => {
  if (!password) return { isStrong: false, checks: {} };
  
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };
  
  return {
    isStrong: Object.values(checks).every(Boolean),
    checks
  };
};

const BusinessSignUpPage = () => {
  const { signUpBusiness, fetchLocalAuthorityUsers } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Form state variables
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [organizationType, setOrganizationType] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [vatNumber, setVatNumber] = useState(''); // New VAT number field
  const [role, setRole] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [sortCode, setSortCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localAuthorityId, setLocalAuthorityId] = useState(null);
  const [localAuthorityName, setLocalAuthorityName] = useState('');
  const [documentFiles, setDocumentFiles] = useState([]);
  const [agreement, setAgreement] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ isStrong: false, checks: {} });

  // Error and loading state
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Local authority state variables
  const [authorities, setAuthorities] = useState([]);
  const [filteredAuthorities, setFilteredAuthorities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');


  // Organization type mapping
  const organizationTypeInfo = {
    "Limited Company": {
      label: "Company Number",
      maxLength: 8,
      inputType: "text",
    },
    "Charity": {
      label: "Charity Number",
      maxLength: 8,
      inputType: "text",
    },
    "Community Interest Company (CIC)": {
      label: "CIC Number",
      maxLength: 8,
      inputType: "text",
    },
    "Sole Trader": {
      label: "UTR/NI Number",
      maxLength: 10,
      inputType: "text",
    },
    "Limited Liability Partnership": {
      label: "Company Number",
      maxLength: 8,
      inputType: "text",
    },
    "Other": {
      label: "Please specify",
      maxLength: null,
      inputType: "text",
    },
  };

  // Fetch local authorities on component mount
  useEffect(() => {
    const fetchAuthorities = async () => {
      try {
        const data = await fetchLocalAuthorityUsers();
        setAuthorities(data);
        setFilteredAuthorities(data);
      } catch (error) {
        // console.error('Error fetching local authorities:', error);
      }
    };
    fetchAuthorities();
  }, [fetchLocalAuthorityUsers]);

  // Filter authorities based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = authorities.filter((auth) =>
        auth.name && auth.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAuthorities(filtered);
    } else {
      setFilteredAuthorities(authorities);
    }
  }, [searchTerm, authorities]);

  const handleLocalAuthoritySearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleAuthoritySelect = (authority) => {
    setLocalAuthorityName(authority.firstName);
    setLocalAuthorityId(authority.id);
    setSearchTerm('');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDocumentFiles((prevFiles) => [...prevFiles, file]);
    }

    // Reset the file input to allow the same file to be selected again if needed
    e.target.value = null;
  };

  const handleRemoveFile = (index) => {
    setDocumentFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };
  
    // Function to handle numeric input with maxLength
    const handleNumericInput = (e, maxLength, setter) => {
      const value = e.target.value.replace(/\D/g, '').slice(0, maxLength);
      setter(value);
    };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    // Validate inputs
    if (!businessName.trim()) newErrors.businessName = 'Please input your business name';
    if (!email.trim()) newErrors.email = 'Please input your email';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Please input a valid email';
    if (!address.trim()) newErrors.address = 'Please input your address';
    if (!postcode.trim()) newErrors.postcode = 'Please input your postcode';
    if (!organizationType) newErrors.organizationType = 'Please select your organization type';
    if (organizationType && !registrationNumber.trim()) {
      newErrors.registrationNumber = `Please input your ${organizationTypeInfo[organizationType]?.label.toLowerCase()}`;
    } else if (organizationType && organizationTypeInfo[organizationType]?.maxLength) {
      if (registrationNumber.length > organizationTypeInfo[organizationType].maxLength) {
        newErrors.registrationNumber = `${organizationTypeInfo[organizationType]?.label} must be at most ${organizationTypeInfo[organizationType].maxLength} characters long`;
      }
    }
    if (!role) newErrors.role = 'Please select your role';
    if (!bankAccountNumber.trim()) {
      newErrors.bankAccountNumber = 'Please input your bank account number';
    } else if (bankAccountNumber.length !== 8) {
      newErrors.bankAccountNumber = 'Bank account number must be 8 digits';
    }
    if (!sortCode.trim()) {
      newErrors.sortCode = 'Please input your bank sort code';
    } else if (sortCode.length !== 6) {
      newErrors.sortCode = 'Bank sort code must be 6 digits';
    }
    if (vatNumber.trim() && vatNumber.length > 9) {
      newErrors.vatNumber = 'VAT number must be at most 9 digits';
    }
    if (!password) {
      newErrors.password = 'Password is required';
    } else {
      const strength = validatePasswordStrength(password);
      if (!strength.isStrong) {
        newErrors.password = 'Password must meet all requirements';
      }
    }
    if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    if (password !== confirmPassword) newErrors.confirmPassword = 'The passwords do not match.';
    if (!agreement) newErrors.agreement = 'You must agree to the terms and conditions.';
    if (documentFiles.length === 0) newErrors.documents = 'Please upload necessary business documents';

    setErrors(newErrors);
    // console.log(errors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    try {
      setLoading(true);
      await signUpBusiness(
        businessName,
        email,
        password,
        address,
        postcode,
        organizationType,
        registrationNumber,
        vatNumber, // Include VAT number
        role,
        bankAccountNumber,
        sortCode,
        documentFiles,
        localAuthorityId
      );
      navigate("/emailverification");
    } catch (error) {
      const customErrorMessage = error.message;
      setErrors({ general: customErrorMessage });
      // console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setPasswordStrength(validatePasswordStrength(e.target.value));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-gray-100 p-3">
      <div className="bg-white bg-opacity-70 backdrop-blur-sm rounded-lg shadow-xl p-6 max-w-2xl w-full">
        <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">Create Your Business Account</h2>
        {errors.general && (
          <div className="mb-4 text-center text-red-600 text-sm">
            {errors.general}
          </div>
        )}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label htmlFor="business-name" className="block text-xs font-medium text-gray-700">Business Name</label>
              <div className="relative">
                <input
                  id="business-name"
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  className="pl-10 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                  placeholder="Your Business Name"
                />
                <BuildingOfficeIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
              </div>
              {errors.businessName && <p className="text-xs text-red-600">{errors.businessName}</p>}
            </div>
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-gray-700">Email Address</label>
              <div className="relative">
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                  placeholder="business@example.com"
                />
                <EnvelopeIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
              </div>
              {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
            </div>
            <div>
              <label htmlFor="address" className="block text-xs font-medium text-gray-700">Address</label>
              <div className="relative">
                <input
                  id="address"
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="pl-10 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                  placeholder="123 Business St"
                />
                <MapPinIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
              </div>
              {errors.address && <p className="text-xs text-red-600">{errors.address}</p>}
            </div>
            <div>
              <label htmlFor="postcode" className="block text-xs font-medium text-gray-700">Postcode</label>
              <input
                id="postcode"
                type="text"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                placeholder="AB12 3CD"
              />
              {errors.postcode && <p className="text-xs text-red-600">{errors.postcode}</p>}
            </div>


          
            <div>
              <label htmlFor="org-type" className="block text-xs font-medium text-gray-700">Type of Organization</label>
              <select
                id="org-type"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7 text-gray-700"
                value={organizationType}
                onChange={(e) => setOrganizationType(e.target.value)}
              >
                <option value="">Select</option>
                <option value="Limited Company">Limited Company</option>
                <option value="Charity">Charity</option>
                <option value="Community Interest Company (CIC)">Community Interest Company (CIC)</option>
                <option value="Sole Trader">Sole Trader</option>
                <option value="Limited Liability Partnership">Limited Liability Partnership</option>
                <option value="Other">Other (please state)</option>
              </select>
              {errors.organizationType && <p className="text-xs text-red-600">{errors.organizationType}</p>}
            </div>
            {/* Registration Number */}
            {organizationType && (
              <div>
                <label htmlFor="reg-number" className="block text-xs font-medium text-gray-700">
                  {organizationTypeInfo[organizationType]?.label}
                </label>
                <input
                  id="reg-number"
                  type="text"
                  value={registrationNumber}
                  onChange={(e) => {
                    if (organizationTypeInfo[organizationType]?.inputType === 'text') {
                      setRegistrationNumber(e.target.value.slice(0, organizationTypeInfo[organizationType]?.maxLength || undefined));
                    }
                  }}
                  maxLength={organizationTypeInfo[organizationType]?.maxLength || undefined}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                  placeholder="Enter number"
                />
                {errors.registrationNumber && <p className="text-xs text-red-600">{errors.registrationNumber}</p>}
              </div>
            )}
            {/* VAT Number */}
            <div>
              <label htmlFor="vat-number" className="block text-xs font-medium text-gray-700">VAT Number (Optional)</label>
              <input
                id="vat-number"
                type="text"
                value={vatNumber}
                onChange={(e) => handleNumericInput(e, 9, setVatNumber)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                placeholder="Enter VAT number (optional)"
              />
              {errors.vatNumber && <p className="text-xs text-red-600">{errors.vatNumber}</p>}
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label htmlFor="role" className="block text-xs font-medium text-gray-700">Your Role</label>
              <select
                id="role"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7 text-gray-700"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="">Select</option>
                <option value="Owner">Owner</option>
                <option value="Staff">Staff</option>
                <option value="Manager">Manager</option>
              </select>
              {errors.role && <p className="text-xs text-red-600">{errors.role}</p>}
            </div>
           
 {/* Bank Account Number */}
 <div>
              <label htmlFor="bank-account" className="block text-xs font-medium text-gray-700">Bank Account Number</label>
              <div className="relative">
                <input
                  id="bank-account"
                  type="text"
                  value={bankAccountNumber}
                  onChange={(e) => handleNumericInput(e, 8, setBankAccountNumber)}
                  className="pl-10 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                  placeholder="12345678"
                />
                <BanknotesIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
              </div>
              {errors.bankAccountNumber && <p className="text-xs text-red-600">{errors.bankAccountNumber}</p>}
            </div>


             {/* Sort Code */}
             <div>
              <label htmlFor="sort-code" className="block text-xs font-medium text-gray-700">Bank Sort Code</label>
              <input
                id="sort-code"
                type="text"
                value={sortCode}
                onChange={(e) => handleNumericInput(e, 6, setSortCode)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                placeholder="123456"
              />
              {errors.sortCode && <p className="text-xs text-red-600">{errors.sortCode}</p>}
            </div>

            
            <div>
              <label htmlFor="password" className="block text-xs font-medium text-gray-700">Password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={handlePasswordChange}
                  className="pl-10 pr-8 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                />
                <LockClosedIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeSlashIcon className="h-3 w-3" /> : <EyeIcon className="h-3 w-3" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-600">{errors.password}</p>}
              
              {/* Password strength indicator */}
              {password && (
                <div className="mt-1">
                  <div className="flex space-x-1 h-1">
                    <div className={`flex-1 rounded-full transition-colors duration-200 ${
                      passwordStrength.checks.length ? 'bg-blue-200' : 'bg-gray-200'
                    }`}></div>
                    <div className={`flex-1 rounded-full transition-colors duration-200 ${
                      (passwordStrength.checks.uppercase && passwordStrength.checks.lowercase) ? 'bg-blue-300' : 'bg-gray-200'
                    }`}></div>
                    <div className={`flex-1 rounded-full transition-colors duration-200 ${
                      passwordStrength.checks.number ? 'bg-blue-400' : 'bg-gray-200'
                    }`}></div>
                    <div className={`flex-1 rounded-full transition-colors duration-200 ${
                      passwordStrength.checks.special ? 'bg-blue-500' : 'bg-gray-200'
                    }`}></div>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">
                    {!passwordStrength.isStrong && "Required: "}
                    {!passwordStrength.checks.length && "8+ characters, "}
                    {!passwordStrength.checks.uppercase && "uppercase, "}
                    {!passwordStrength.checks.lowercase && "lowercase, "}
                    {!passwordStrength.checks.number && "number, "}
                    {!passwordStrength.checks.special && "special char"}
                    {passwordStrength.isStrong && "Strong password"}
                  </p>
                </div>
              )}
            </div>
            <div>
              <label htmlFor="confirm-password" className="block text-xs font-medium text-gray-700">Confirm Password</label>
              <div className="relative">
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7"
                />
                <LockClosedIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
              </div>
              {errors.confirmPassword && <p className="text-xs text-red-600">{errors.confirmPassword}</p>}
            </div>

            {/* <label htmlFor="local-authority" className="block text-xs font-medium text-gray-700">Local Authority</label>
            <option value="none">None</option>

              <select
                id="local-authority"
                value={localAuthorityId || ''}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const selectedAuthority = authorities.find(auth => auth.id === selectedId);
                  setLocalAuthorityId(selectedId || null);
                  setLocalAuthorityName(selectedAuthority ? selectedAuthority.name : '');
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring-blue-200 focus:ring-opacity-50 text-xs h-7 text-gray-700 bg-white"
              >
                <option value="">Select Authority</option>
                {filteredAuthorities.map((authority) => (
                  <option key={authority.id} value={authority.id}>
                    {authority.firstName} {authority.lastName}
                  </option>
                ))}
              </select>
              {localAuthorityName && (
                <p className="mt-1 text-xs text-blue-600">Selected: {localAuthorityName}</p>
              )}
              {errors.localAuthority && <p className="text-xs text-red-600">{errors.localAuthority}</p>}
          */}
            </div>
          

          <div className="col-span-full space-y-3">
            <div>
              <label htmlFor="document-upload" className="block text-xs font-medium text-gray-700">Upload safeguarding policy and liability if applicable</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                      <span>Upload a file</span>
                      <input
  id="file-upload"
  name="file-upload"
  type="file"
  className="sr-only"
  onChange={handleFileUpload}
  ref={fileInputRef}
/>
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PNG, JPG, PDF up to 10MB</p>
                </div>
              </div>
              {documentFiles.length > 0 && (
  <ul className="text-xs text-gray-600 mt-2">
    {documentFiles.map((file, index) => (
      <li key={index} className="flex items-center justify-between">
        {file.name}
        <button
          type="button"
          onClick={() => handleRemoveFile(index)}
          className="text-red-600 hover:text-red-800 text-xs"
        >
          Remove
        </button>
      </li>
    ))}
  </ul>
)}

              {errors.documents && <p className="text-xs text-red-600">{errors.documents}</p>}
            </div>
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="terms"
                  type="checkbox"
                  className="h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={agreement}
                  onChange={(e) => setAgreement(e.target.checked)}
                />
              </div>
              <div className="ml-2">
                <label htmlFor="terms" className="text-xs text-gray-700">
                  I agree to the{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Terms and Conditions
                  </a>
                  {' '}and{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>
            </div>
            {errors.agreement && <p className="text-xs text-red-600">{errors.agreement}</p>}
          </div>

          <div className="col-span-full mt-4">
            <button
              type="submit"
              disabled={loading}
              className={`w-full flex justify-center py-1.5 px-3 border border-transparent rounded-md shadow-sm text-xs font-medium text-white ${
                loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            >
              {loading ? 'Signing Up...' : 'Create Business Account'}
            </button>
          </div>
        </form>

        <p className="mt-4 text-center text-xs text-gray-600">
          Already have an account? <a href="/login" className="font-medium text-blue-600 hover:underline">Sign in</a>
        </p>
      </div>
    </div>
  );
};

export default BusinessSignUpPage;
